import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, User, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { friendlyAuthMessage } from '../utils/authErrorMessage';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, signup, loginWithGoogle } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await signup(email, password, nickname);
      }
    } catch (err) {
      setError(friendlyAuthMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      await loginWithGoogle();
    } catch (err) {
      setError(friendlyAuthMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-jj-canvas dark:bg-jj-canvas-dark flex items-center justify-center p-4 sm:p-8">
      <div className="absolute inset-0 pointer-events-none opacity-40 dark:opacity-25">
        <div className="absolute -top-24 -right-10 h-72 w-72 rounded-full bg-teal-200/35 blur-3xl dark:bg-teal-900/25" />
        <div className="absolute -bottom-28 -left-16 h-80 w-80 rounded-full bg-amber-100/40 blur-3xl dark:bg-amber-900/15" />
      </div>

      <div className="relative w-full max-w-md rounded-jj-xl bg-jj-surface/96 dark:bg-jj-surface-dark-2/96 ring-1 ring-black/[0.06] dark:ring-white/[0.1] shadow-jj dark:shadow-jj-dark backdrop-blur-xl backdrop-saturate-150 overflow-hidden">
        <div className="px-6 sm:px-8 pt-9 sm:pt-10 pb-7 sm:pb-8 text-center border-b border-black/[0.05] dark:border-white/[0.08]">
          <div className="mx-auto mb-5 h-16 w-16 rounded-jj-lg overflow-hidden ring-1 ring-black/[0.06] dark:ring-white/10 shadow-jj-card dark:shadow-none">
            <img
              src={`${process.env.PUBLIC_URL}/LogoHeader.png`}
              alt="Jamā’ah Journal"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-jj-muted dark:text-stone-500 font-medium">
            Welcome
          </p>
          <h1 className="mt-2 text-2xl font-semibold tracking-[-0.02em] text-jj-ink dark:text-stone-50">
            Jamā&apos;ah Journal
          </h1>
          <p className="mt-2 text-sm text-jj-muted dark:text-stone-400 leading-relaxed">
            A quiet place to track salāh with care—no noise, no performance.
          </p>
        </div>

        <div className="p-6 sm:p-8">
          <div className="flex rounded-jj-lg border border-jj-border/75 dark:border-white/[0.08] p-1 bg-jj-mist/70 dark:bg-white/[0.04] ring-1 ring-black/[0.03] dark:ring-white/[0.05]">
            <button
              type="button"
              onClick={() => {
                setIsLogin(true);
                setError('');
              }}
              className={`jj-interactive-press flex-1 rounded-xl py-2.5 text-sm font-medium transition-colors duration-jj ease-jj-out focus:outline-none focus-visible:ring-2 focus-visible:ring-jj-accent ${
                isLogin
                  ? 'bg-white dark:bg-jj-elevated-dark text-jj-ink dark:text-stone-100 shadow-sm'
                  : 'text-jj-muted dark:text-stone-400'
              }`}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => {
                setIsLogin(false);
                setError('');
              }}
              className={`jj-interactive-press flex-1 rounded-xl py-2.5 text-sm font-medium transition-colors duration-jj ease-jj-out focus:outline-none focus-visible:ring-2 focus-visible:ring-jj-accent ${
                !isLogin
                  ? 'bg-white dark:bg-jj-elevated-dark text-jj-ink dark:text-stone-100 shadow-sm'
                  : 'text-jj-muted dark:text-stone-400'
              }`}
            >
              Create account
            </button>
          </div>

          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="mt-5 rounded-2xl border border-red-200/80 dark:border-red-900/50 bg-red-50/80 dark:bg-red-950/35 px-4 py-3 text-sm text-red-900 dark:text-red-100"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="relative">
              <Mail
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-jj-muted dark:text-stone-500 w-5 h-5"
                strokeWidth={1.75}
              />
              <input
                type="email"
                placeholder="Email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-2xl border border-jj-border dark:border-white/10 bg-white dark:bg-black text-jj-ink dark:text-stone-100 placeholder:text-stone-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-jj-accent"
                required
              />
            </div>

            {!isLogin && (
              <div className="relative">
                <User
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-jj-muted dark:text-stone-500 w-5 h-5"
                  strokeWidth={1.75}
                />
                <input
                  type="text"
                  placeholder="Nickname (shown to friends)"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-2xl border border-jj-border dark:border-white/10 bg-white dark:bg-black text-jj-ink dark:text-stone-100 placeholder:text-stone-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-jj-accent"
                  required
                  maxLength={20}
                />
              </div>
            )}

            <div className="relative">
              <Lock
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-jj-muted dark:text-stone-500 w-5 h-5"
                strokeWidth={1.75}
              />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-11 pr-12 py-3 rounded-2xl border border-jj-border dark:border-white/10 bg-white dark:bg-black text-jj-ink dark:text-stone-100 placeholder:text-stone-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-jj-accent"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-jj-muted hover:text-jj-ink dark:hover:text-stone-200"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="jj-interactive-press min-h-[3.25rem] w-full py-3.5 rounded-2xl bg-jj-accent dark:bg-teal-600 text-white font-medium hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-jj-accent focus-visible:ring-offset-2 dark:focus-visible:ring-offset-jj-surface-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-jj ease-jj-out inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-[1.125rem] h-[1.125rem] shrink-0 animate-spin" aria-hidden />
                  <span>Please wait…</span>
                </>
              ) : isLogin ? (
                'Sign in'
              ) : (
                'Create account'
              )}
            </button>
          </form>

          <div className="mt-7">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-jj-border/70 dark:border-white/10" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-jj-surface dark:bg-jj-surface-dark text-jj-muted dark:text-stone-500">
                  or
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              aria-busy={loading}
              className="jj-interactive-press mt-5 min-h-[3.25rem] w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl border border-jj-border dark:border-white/10 bg-white dark:bg-black text-jj-ink dark:text-stone-100 hover:bg-stone-50 dark:hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-jj-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-jj ease-jj-out"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 shrink-0 animate-spin text-jj-muted dark:text-stone-400" aria-hidden />
              ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              )}
              {loading ? 'Connecting…' : 'Continue with Google'}
            </button>
          </div>

          <p className="mt-8 text-center text-sm text-jj-muted dark:text-stone-400">
            {isLogin ? 'New here? ' : 'Already have an account? '}
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="font-medium text-jj-accent dark:text-teal-300 hover:underline"
            >
              {isLogin ? 'Create an account' : 'Sign in instead'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
